import { Traversing } from './Traversing';
import { Collection } from '../Collection';
export class HTML extends Traversing {
    constructor(selector) {
        super(selector);
    }
    dataAttributeName(name) {
        return 'data-' + name.replace(/[A-Z]/g, (match) => '-' + match.toLowerCase());
    }
    getInsertionItems(itemsArray) {
        const collections = Array.isArray(itemsArray) ? itemsArray : [itemsArray];
        const items = [];
        collections.forEach((collection) => {
            collection.forEach((item) => {
                items.push(item);
            });
        });
        return items;
    }
    /**
     * Remove the *Elem* for the DOM.
     *
     * @category HTML manipulation
     *
     */
    remove() {
        this.elements.forEach((item) => {
            item.remove();
        });
        return this;
    }
    /**
     * Clear all DOM children of the *Elem*
     *
     * @category HTML manipulation
     *
     */
    empty() {
        this.elements.forEach((item) => {
            while (item.firstChild)
                item.removeChild(item.firstChild);
        });
        return this;
    }
    /**
     * Return a clone of the collection
     *
     * @category HTML manipulation
     *
     */
    clone() {
        let collection = new Collection();
        this.elements.forEach((item) => {
            collection.add(item.cloneNode(true));
        });
        return collection;
    }
    /**
     * Insert content, specified by the parameter, to the beginning of each element in the collection
     *
     * @category HTML manipulation
     *
     */
    prepend(itemsArray) {
        const sourceItems = this.getInsertionItems(itemsArray);
        this.elements.forEach((target, targetIndex) => {
            const shouldClone = targetIndex < this.elements.length - 1;
            const fragment = document.createDocumentFragment();
            sourceItems.forEach((item) => {
                fragment.appendChild(shouldClone ? item.cloneNode(true) : item);
            });
            target.insertBefore(fragment, target.firstChild);
        });
        return this;
    }
    /**
     * Insert content, specified by the parameter, to the end of each element in the collection
     *
     * @category HTML manipulation
     *
     */
    append(itemsArray) {
        const sourceItems = this.getInsertionItems(itemsArray);
        this.elements.forEach((target, targetIndex) => {
            const shouldClone = targetIndex < this.elements.length - 1;
            const fragment = document.createDocumentFragment();
            sourceItems.forEach((item) => {
                fragment.appendChild(shouldClone ? item.cloneNode(true) : item);
            });
            target.appendChild(fragment);
        });
        return this;
    }
    /**
     * Add an element before specified element
     *
     * @category HTML manipulation
     */
    before(itemsArray) {
        const sourceItems = this.getInsertionItems(itemsArray);
        this.elements.forEach((target, targetIndex) => {
            const parent = target.parentNode;
            if (!parent)
                return;
            const shouldClone = targetIndex < this.elements.length - 1;
            const fragment = document.createDocumentFragment();
            sourceItems.forEach((item) => {
                fragment.appendChild(shouldClone ? item.cloneNode(true) : item);
            });
            parent.insertBefore(fragment, target);
        });
        return this;
    }
    /**
     * Add an element after specified element
     *
     * @category HTML manipulation
     */
    after(itemsArray) {
        const sourceItems = this.getInsertionItems(itemsArray);
        this.elements.forEach((target, targetIndex) => {
            const parent = target.parentNode;
            if (!parent)
                return;
            const shouldClone = targetIndex < this.elements.length - 1;
            const fragment = document.createDocumentFragment();
            const referenceNode = target.nextSibling;
            sourceItems.forEach((item) => {
                fragment.appendChild(shouldClone ? item.cloneNode(true) : item);
            });
            parent.insertBefore(fragment, referenceNode);
        });
        return this;
    }
    /**
     * Wrap each element in the collection with specified element
     *
     * @category HTML manipulation
     *
     */
    wrap(wrapper) {
        this.elements.forEach((item) => {
            if (wrapper.length == 0)
                return;
            var wrapperCopy = wrapper.get(0).cloneNode();
            if (item.parentNode) {
                item.parentNode.insertBefore(wrapperCopy, item);
                wrapperCopy.appendChild(item);
            }
        });
        return this;
    }
    val(value) {
        if (typeof value != 'string' && typeof value != 'number') {
            if (this.elements.length == 0)
                return '';
            return this.elements[0].value;
        }
        else {
            this.elements.forEach((item) => {
                item.value = value.toString();
            });
            return this;
        }
    }
    html(value, append) {
        if (typeof value != 'string' && typeof value != 'number') {
            if (this.elements.length == 0)
                return '';
            return this.elements[0].innerHTML;
        }
        else {
            this.elements.forEach((item) => {
                if (append) {
                    item.innerHTML += value.toString();
                }
                else {
                    item.innerHTML = value.toString();
                }
            });
            return this;
        }
    }
    text(value) {
        if (typeof value != 'string' && typeof value != 'number') {
            if (this.elements.length == 0)
                return '';
            return this.elements[0].textContent || '';
        }
        else {
            this.elements.forEach((item) => {
                item.textContent = value.toString();
            });
            return this;
        }
    }
    attr(name, value) {
        if (typeof name == 'object') {
            this.elements.forEach((item) => {
                for (let attributeName in name) {
                    item.setAttribute(attributeName, name[attributeName]);
                }
            });
            return this;
        }
        if (value === undefined) {
            if (this.elements.length == 0)
                return '';
            return this.elements[0].getAttribute(name) || '';
        }
        else {
            this.elements.forEach((item) => {
                item.setAttribute(name, value);
            });
            return this;
        }
    }
    prop(name, value) {
        if (value === undefined) {
            if (this.elements.length == 0)
                return undefined;
            return this.elements[0][name];
        }
        this.elements.forEach((item) => {
            item[name] = value;
        });
        return this;
    }
    /**
     * Remove a property on each element in the set.
     *
     * @category HTML manipulation
     *
     */
    removeProp(name) {
        this.elements.forEach((item) => {
            delete item[name];
        });
        return this;
    }
    /**
     * Remove an attribute on each element in the set.
     *
     * @category HTML manipulation
     *
     */
    removeAttr(name) {
        this.elements.forEach((item) => {
            item.removeAttribute(name);
        });
        return this;
    }
    data(name, value) {
        if (value === undefined) {
            if (this.elements.length == 0)
                return '';
            return this.elements[0].dataset[name] || '';
        }
        else {
            this.elements.forEach((item) => {
                item.dataset[name] = value;
            });
            return this;
        }
    }
    /**
     * Remove a data attribute on each element in the set.
     *
     * @category HTML manipulation
     *
     */
    removeData(name) {
        this.elements.forEach((item) => {
            delete item.dataset[name];
            item.removeAttribute(this.dataAttributeName(name));
        });
        return this;
    }
}
